import * as React from 'react';

import { PortalTextMainHamburgerMenu } from '../../Models/Api/strongbox.financialportal';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faAngleDown,
    faCheck,
} from '@awesome.me/kit-8a0d2fd588/icons/modules/classic/light';

import '../Main.scss';
import '../PortalHeader.scss';

type Props = {
    logout?: () => void;
    settings?: () => void;
    dataCollectionReport?: () => void;
    notificationSettings?: () => void;
    onLanguageCodeChange?: (languageCode: string) => void;
    canAccessSettings: boolean;
    canAccessGlobalSubmissions: boolean;
    canChangeLanguage: boolean;
    hamburgerText?: PortalTextMainHamburgerMenu;
    activeLanguageCode: string;
    /** Text of the button that opens the menu, i.e. the logged in user's name. */
    label?: string;
};

export const FinPortalDropdown: React.FC<Props> = (props): React.ReactElement => {
    const {
        hamburgerText,
        onLanguageCodeChange,
        activeLanguageCode,
        label,
    } = props;

    const [menuOpen, setMenuOpen] = React.useState<boolean>(false);
    const containerRef = React.useRef<HTMLDivElement>(null);

    // Dismiss on an outside click or Escape.
    React.useEffect(() => {
        if (!menuOpen) {
            return;
        }
        const onMouseDown = (e: MouseEvent): void => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setMenuOpen(false);
            }
        };
        const onKeyDown = (e: KeyboardEvent): void => {
            if (e.key === 'Escape') {
                setMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', onMouseDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onMouseDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [menuOpen]);

    const menuItem = (key: string, text: string, onClick?: () => void, leading?: React.ReactNode): React.ReactElement => (
        <button
            key={key}
            type={'button'}
            role={'menuitem'}
            className={'cr-menu-item'}
            onClick={() => {
                setMenuOpen(false);
                onClick && onClick();
            }}
        >
            {leading !== undefined && <span className={'cr-menu-item__leading'}>{leading}</span>}
            <span className={'cr-menu-item__label'}>{text}</span>
        </button>
    );

    return (
        <div className={'page-app-header__user'} ref={containerRef}>
            <button
                type={'button'}
                className={'cr-btn cr-btn--secondary'}
                aria-haspopup={'menu'}
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen(!menuOpen)}
            >
                <span className={'cr-btn__label'}>{label}</span>
                <span className={'cr-btn__icon'}><FontAwesomeIcon icon={faAngleDown} /></span>
            </button>
            {menuOpen && (
                <div className={'page-app-header__menu cr-menu'} role={'menu'}>
                    {props.canAccessSettings && menuItem('settings', hamburgerText?.userManagement ?? '', props.settings)}
                    {props.canAccessGlobalSubmissions && menuItem('dcr', hamburgerText?.dataCollectionReport ?? '', props.dataCollectionReport)}
                    {menuItem('notifications', hamburgerText?.notificationSettings ?? '', props.notificationSettings)}
                    {
                        props.canChangeLanguage && !!hamburgerText && (
                            <React.Fragment>
                                <div className={'cr-menu-separator'} role={'separator'} />
                                <div className={'cr-menu-section-header'}>{hamburgerText.portalLanguage || ''}</div>
                                {
                                    hamburgerText.portalLanguages.map((language, index) => menuItem(
                                        `language-${index}`,
                                        language.description,
                                        () => { !!onLanguageCodeChange && !!language.languageCode && onLanguageCodeChange(language.languageCode); },
                                        activeLanguageCode === language.languageCode ? (<FontAwesomeIcon icon={faCheck} />) : (<></>)
                                    ))
                                }
                            </React.Fragment>
                        )
                    }
                    <div className={'cr-menu-separator'} role={'separator'} />
                    {menuItem('logout', hamburgerText?.logout ?? '', props.logout)}
                </div>
            )}
        </div>
    );
}
